import { FieldWrapper } from '@fukuri-admins/ui/common';
import { extractErrorMessages } from '@fukuri-admins/ui/common/Form/logics/extractErrorMessages';
import { type ComponentProps } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { PaginatedSelect } from '..';
import type { OptionType } from '../type';

type PaginatedSelectProps = ComponentProps<typeof PaginatedSelect>;

type Props = {
  /** フォームフィールド名 */
  name: string;
  /** PaginatedSelect 固有のプロパティ（value / onChange / onBlur を除く） */
  paginatedSelect: Omit<PaginatedSelectProps, 'value' | 'onChange' | 'onBlur'>;
  /** FieldWrapper のラベル設定 */
  fieldWrapperProps: {
    labelType: ComponentProps<typeof FieldWrapper>['labelType'];
    labelText: ComponentProps<typeof FieldWrapper>['labelText'];
  };
};

/**
 * PaginatedSelect を react-hook-form の Controller でラップしたコンポーネント。
 * onChange の RHF 連携（ID 文字列のみ登録）・エラーメッセージ表示（FieldWrapper）を内包する。
 * 表示状態は react-select の内部 state に任せる（既存 Form と同じ非制御）。
 */
export const PaginatedSelectController: React.FC<Props> = ({
  name,
  paginatedSelect,
  fieldWrapperProps,
}) => {
  const { control } = useFormContext();

  const {
    field: { onChange, onBlur },
    fieldState: { error },
  } = useController({ control, name });

  const handleChange = (option: OptionType | null) => {
    onChange(option ? String(option.value) : '');
  };

  return (
    <FieldWrapper
      htmlFor={name}
      labelType={fieldWrapperProps.labelType}
      labelText={fieldWrapperProps.labelText}
      errorMessages={extractErrorMessages(error)}
    >
      <PaginatedSelect {...paginatedSelect} onChange={handleChange} onBlur={onBlur} />
    </FieldWrapper>
  );
};
